/**
 * Layout Component – Modern SaaS Admin Dashboard
 * Dark sidebar + clean top-bar + smooth collapsible navigation
 */

import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  UserCog,
  LogOut,
  X,
  Calendar,
  FolderTree,
  Briefcase,
  Settings,
  FileText,
  User,
  HelpCircle,
  CreditCard,
  AlignJustify,
  ChevronDown,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import Modal from './Modal';
import Button from './Button';

type NavGroup = 'main' | 'content' | 'system';

interface NavChild {
  name: string;
  to: string;
}

interface NavItem {
  name: string;
  icon: React.ComponentType<{ className?: string }>;
  group: NavGroup;
  to?: string;
  children?: NavChild[];
}

const navigation: NavItem[] = [
  { name: 'Dashboard', to: '/dashboard', icon: LayoutDashboard, group: 'main' },
  { name: 'Customers', to: '/customers', icon: Users, group: 'main' },
  { name: 'Providers', to: '/providers', icon: UserCog, group: 'main' },
  { name: 'Bookings', to: '/bookings', icon: Calendar, group: 'main' },
  { name: 'Categories', to: '/categories', icon: FolderTree, group: 'content' },
  { name: 'Services', to: '/services', icon: Briefcase, group: 'content' },
  {
    name: 'Revenue',
    icon: CreditCard,
    group: 'content',
    children: [
      { name: 'Transactions', to: '/transactions' },
      { name: 'Subscriptions', to: '/user-subscriptions' },
    ],
  },
    { name: 'Subscription Plans', to: '/subscription-plans', icon: CreditCard, group: 'content' },
  { name: 'Support', to: '/support', icon: HelpCircle, group: 'content' },
  { name: 'Content', to: '/content', icon: FileText, group: 'content' },
  { name: 'Settings', to: '/settings', icon: Settings, group: 'system' },
  { name: 'Profile', to: '/profile', icon: User, group: 'system' },
];

const groups = [
  { key: 'main', label: 'Main' },
  { key: 'content', label: 'Manage' },
  { key: 'system', label: 'System' },
];

export default function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState<Record<string, boolean>>({});
  const { user, logout } = useAuth();

  const handleLogoutClick = () => setIsLogoutModalOpen(true);
  const handleProfileClick = () => navigate('/profile');
  const handleLogoClick = () => {
    navigate('/dashboard');
    window.location.reload();
  };
  const handleConfirmLogout = () => {
    logout();
    setIsLogoutModalOpen(false);
  };

  const isChildPathActive = (children?: NavChild[]) =>
    !!children?.some((child) => location.pathname.startsWith(child.to));

  useEffect(() => {
    navigation.forEach((item) => {
      if (item.children?.some((child) => location.pathname.startsWith(child.to))) {
        setExpandedMenus((prev) => ({ ...prev, [item.name]: true }));
      }
    });
  }, [location.pathname]);

  const userInitial = user?.name?.charAt(0)?.toUpperCase() || 'A';

  return (
    <div className="min-h-screen bg-[#f5f6fa] overflow-x-hidden">
      {/* ── Mobile Overlay ── */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 z-50 h-full flex flex-col
          ${isCollapsed ? 'w-[70px]' : 'w-[240px]'}
          transition-all duration-300 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0
        `}
        style={{
          background: 'linear-gradient(180deg, #1e1b4b 0%, #2e2b6e 60%, #3730a3 100%)',
        }}
      >
        {/* Logo / Brand */}
        <div
          className={`
            flex items-center h-16 border-b border-white/10 flex-shrink-0
            ${isCollapsed ? 'justify-center px-4' : 'justify-between px-5'}
          `}
        >
          {!isCollapsed && (
            <button onClick={handleLogoClick} className="flex items-center gap-2 min-w-0">
              <img
                src="/logo1.png"
                alt="Logo"
                className="h-9 w-auto object-contain flex-shrink-0"
                loading="eager"
              />
            </button>
          )}
          {/* Desktop collapse toggle */}
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className={`
              hidden lg:flex items-center justify-center w-8 h-8 rounded-lg
              text-white/60 hover:text-white hover:bg-white/10
              transition-colors duration-150 flex-shrink-0
            `}
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <AlignJustify className="w-5 h-5" />
          </button>
          {/* Mobile close */}
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden text-white/60 hover:text-white transition-colors p-1"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto scrollbar-thin py-4 px-3 space-y-5">
          {groups.map((group) => {
            const items = navigation.filter((n) => n.group === group.key);
            return (
              <div key={group.key}>
                {!isCollapsed && (
                  <p className="px-3 mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-white/35">
                    {group.label}
                  </p>
                )}
                <div className="space-y-0.5">
                  {items.map((item) => {
                    if (item.children) {
                      const isActiveParent = isChildPathActive(item.children);
                      const isExpanded = expandedMenus[item.name] ?? isActiveParent;

                      return (
                        <div key={item.name} className="space-y-0.5">
                          <button
                            type="button"
                            title={isCollapsed ? item.name : undefined}
                            className={`group w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-150 cursor-pointer ${
                              isCollapsed ? 'justify-center' : ''
                            } ${
                              isActiveParent
                                ? 'bg-white/15 text-white shadow-sm'
                                : 'text-white/60 hover:text-white hover:bg-white/10'
                            }`}
                            onClick={() => {
                              if (isCollapsed) {
                                setIsCollapsed(false);
                              }
                              setExpandedMenus((prev) => ({
                                ...prev,
                                [item.name]: !isExpanded,
                              }));
                            }}
                          >
                            <item.icon
                              className={`flex-shrink-0 transition-colors ${
                                isCollapsed ? 'w-5 h-5' : 'w-4 h-4'
                              } ${
                                isActiveParent
                                  ? 'text-white'
                                  : 'text-white/60 group-hover:text-white'
                              }`}
                            />
                            {!isCollapsed && <span className="truncate">{item.name}</span>}
                            {!isCollapsed && (
                              <ChevronDown
                                className={`ml-auto w-4 h-4 transition-transform ${
                                  isExpanded ? 'rotate-180 text-white/90' : 'text-white/60'
                                }`}
                              />
                            )}
                          </button>

                          {!isCollapsed && isExpanded && (
                            <div className="pl-7 space-y-0.5">
                              {item.children.map((child) => (
                                <NavLink
                                  key={child.to}
                                  to={child.to}
                                  onClick={() => setSidebarOpen(false)}
                                  className={({ isActive }) =>
                                    `group flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all duration-150 ${
                                      isActive
                                        ? 'text-white bg-white/12'
                                        : 'text-white/60 hover:text-white hover:bg-white/10'
                                    }`
                                  }
                                >
                                  {({ isActive }) => (
                                    <>
                                      <span
                                        className={`w-1.5 h-1.5 rounded-full ${
                                          isActive ? 'bg-white/90' : 'bg-white/40'
                                        }`}
                                      />
                                      <span className="truncate">{child.name}</span>
                                    </>
                                  )}
                                </NavLink>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    }

                    return (
                      <NavLink
                        key={item.name}
                        to={item.to || '/dashboard'}
                        title={isCollapsed ? item.name : undefined}
                        className={({ isActive }) =>
                          `group flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium
                         transition-all duration-150 cursor-pointer
                         ${isCollapsed ? 'justify-center' : ''}
                         ${
                           isActive
                             ? 'bg-white/15 text-white shadow-sm'
                             : 'text-white/60 hover:text-white hover:bg-white/10'
                         }`
                        }
                        onClick={() => setSidebarOpen(false)}
                      >
                        {({ isActive }) => (
                          <>
                            <item.icon
                              className={`flex-shrink-0 transition-colors ${
                                isCollapsed ? 'w-5 h-5' : 'w-4 h-4'
                              } ${isActive ? 'text-white' : 'text-white/60 group-hover:text-white'}`}
                            />
                            {!isCollapsed && <span className="truncate">{item.name}</span>}
                            {!isCollapsed && isActive && (
                              <span className="ml-auto w-1.5 h-1.5 rounded-full bg-white/80 flex-shrink-0" />
                            )}
                          </>
                        )}
                      </NavLink>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </nav>

        {/* User / Logout footer */}
        <div className={`p-3 border-t border-white/10 flex-shrink-0`}>
          <div className={`flex items-center gap-3 ${isCollapsed ? 'justify-center' : ''}`}>
            {/* Avatar */}
            <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
              <span className="text-white text-sm font-bold">{userInitial}</span>
            </div>

            {!isCollapsed && (
              <>
                <button
                  type="button"
                  onClick={handleProfileClick}
                  className="min-w-0 flex-1 text-left"
                  title="View profile"
                >
                  <p className="text-sm font-semibold text-white truncate leading-tight">
                    {user?.name || 'Admin'}
                  </p>
                  <p className="text-xs text-white/50 truncate">{user?.email}</p>
                </button>
                <button
                  onClick={handleLogoutClick}
                  className="p-1.5 rounded-lg text-white/50 hover:text-white hover:bg-white/10 transition-colors flex-shrink-0"
                  title="Logout"
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </>
            )}

            {isCollapsed && (
              <button
                onClick={handleLogoutClick}
                className="p-1.5 rounded-lg text-white/50 hover:text-white hover:bg-white/10 transition-colors"
                title="Logout"
              >
                <LogOut className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </aside>

      {/* ══════════════════════════════════════════
          MAIN AREA
      ══════════════════════════════════════════ */}
      <div
        className={`flex flex-col min-h-screen transition-all duration-300 ${
          isCollapsed ? 'lg:pl-[70px]' : 'lg:pl-[240px]'
        }`}
      >
        {/* ── Top Navbar ── */}

        {/* ── Page Content ── */}
        <main className="flex-1 p-4 lg:p-6 animate-fade-in overflow-x-hidden">
          <Outlet />
        </main>
      </div>

      {/* ── Logout Modal ── */}
      <Modal
        isOpen={isLogoutModalOpen}
        onClose={() => setIsLogoutModalOpen(false)}
        title="Sign out"
        description="Are you sure you want to sign out of your account?"
        size="sm"
      >
        <div className="flex gap-3 justify-end">
          <Button variant="secondary" onClick={() => setIsLogoutModalOpen(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirmLogout}>
            Sign out
          </Button>
        </div>
      </Modal>
    </div>
  );
}
